import React, { useState, useEffect } from 'react';
import { XMarkIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { useTaskStore } from '../../stores/taskStore';
import { TaskPanel } from './TaskPanel';

interface TaskProgressCardProps {
  taskId: string;
}

const TaskProgressCard: React.FC<TaskProgressCardProps> = ({ taskId }) => {
  const { tasks, removeTask } = useTaskStore();
  const task = tasks.get(taskId);
  
  if (!task) return null;
  
  const getStatusColor = () => {
    switch (task.status) {
      case 'completed':
        return 'bg-green-500';
      case 'failed':
        return 'bg-red-500';
      case 'processing':
        return 'bg-blue-500';
      case 'retrying':
        return 'bg-yellow-500';
      default:
        return 'bg-gray-500';
    }
  };
  
  const getTaskTypeLabel = () => {
    switch (task.task_type) {
      case 'image_fetch_single':
        return 'Fetching images for 1 property';
      case 'image_fetch_bulk':
        return `Fetching images for ${task.progress.total} properties`;
      case 'image_fetch_all':
        return 'Fetching images for all properties';
      default:
        return task.task_type;
    }
  };
  
  const canDismiss = ['completed', 'failed', 'cancelled'].includes(task.status);
  
  return (
    <div className="bg-white rounded-lg shadow-lg p-4 w-[640px] border border-gray-200">
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1">
          <h4 className="text-sm font-medium text-gray-900">
            {getTaskTypeLabel()}
          </h4>
          <p className="text-xs text-gray-500 mt-1">
            {task.progress.message || `Status: ${task.status}`}
          </p>
        </div>
        {canDismiss && (
          <button
            onClick={() => removeTask(taskId)}
            className="ml-2 text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        )}
      </div>
      
      {task.status === 'processing' && task.progress.total > 0 && (
        <div className="mt-3">
          <div className="flex justify-between text-xs text-gray-600 mb-1">
            <span>{task.progress.current} of {task.progress.total}</span>
            <span>{Math.round(task.progress.percentage)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={clsx(
                'h-2 rounded-full transition-all duration-300',
                getStatusColor()
              )}
              style={{ width: `${task.progress.percentage}%` }}
            />
          </div>
        </div>
      )}
      
      {task.error_message && (
        <div className="mt-2 text-xs text-red-600 bg-red-50 p-2 rounded">
          {task.error_message}
        </div>
      )}
      
      {task.retry_count > 0 && task.status === 'retrying' && (
        <div className="mt-2 text-xs text-yellow-600">
          Retry attempt {task.retry_count}
        </div>
      )}
    </div>
  );
};

export const TaskProgressIndicator: React.FC = () => {
  const { activeTasks, tasks, getDebugInfo, cleanupStaleActiveTasks } = useTaskStore();
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [hasAnyTasks, setHasAnyTasks] = useState(false);
  
  // Debug function to log current state
  const logDebugInfo = () => {
    const debugInfo = getDebugInfo();
    console.log('TaskProgressIndicator Debug Info:', debugInfo);
    cleanupStaleActiveTasks();
  };
  
  // Check for any tasks that need attention
  useEffect(() => {
    const allTasksArray = Array.from(tasks.values());
    setHasAnyTasks(allTasksArray.length > 0);
  }, [tasks]);
  
  // Auto-close panel when no tasks exist
  useEffect(() => {
    if (isPanelOpen && tasks.size === 0) {
      const timer = setTimeout(() => setIsPanelOpen(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isPanelOpen, tasks.size]);
  
  // Show indicator if there are any tasks or if panel is open
  const shouldShow = tasks.size > 0 || isPanelOpen;
  
  if (!shouldShow) return null;
  
  const totalTasks = tasks.size;
  const activeTaskCount = activeTasks.length;
  const completedTasks = Array.from(tasks.values()).filter(
    task => task.status === 'completed'
  ).length;
  const failedTasks = Array.from(tasks.values()).filter(
    task => task.status === 'failed'
  ).length;

  return (
    <>
      {/* Single Task Manager Button */}
      {!isPanelOpen && (
        <div className="absolute bottom-0 right-4 py-4">
          <button
            onClick={() => setIsPanelOpen(true)}
            onDoubleClick={logDebugInfo}
            className="bg-gray-800 hover:bg-gray-700 text-white text-sm px-4 py-3 rounded-lg transition-colors cursor-pointer flex items-center gap-3 min-w-[240px] shadow-lg"
            title="Click to open task manager, double-click for debug info"
          >
            <div className="flex items-center gap-2">
              {/* Status indicator */}
              <div className={clsx(
                "w-2 h-2 rounded-full",
                activeTaskCount > 0 ? "bg-blue-400 animate-pulse" : 
                failedTasks > 0 ? "bg-red-400" :
                completedTasks > 0 ? "bg-green-400" : "bg-gray-400"
              )} />
              
              <span className="font-medium">Task Manager</span>
            </div>
            
            <div className="flex-1 text-right text-xs opacity-75">
              {activeTaskCount > 0 && `${activeTaskCount} active`}
              {activeTaskCount > 0 && totalTasks > activeTaskCount && " • "}
              {totalTasks > activeTaskCount && `${totalTasks - activeTaskCount} completed`}
              {totalTasks === 0 && "No tasks"}
            </div>
            
            <ChevronUpIcon className="w-4 h-4 opacity-75" />
          </button>
        </div>
      )}
      
      {/* Expandable Task Panel */}
      <TaskPanel 
        isOpen={isPanelOpen} 
        onClose={() => setIsPanelOpen(false)} 
      />
    </>
  );
};